import { FONTS } from './constants';

/**
 * Arabic Unicode range regex for RTL detection
 */
const ARABIC_REGEX = /[\u0600-\u06FF]/;

/**
 * Detects if text contains Arabic/RTL characters
 */
export function isArabicText(text: string): boolean {
  return ARABIC_REGEX.test(text);
}

/**
 * Gets text direction based on content
 */
export function getTextDirection(text: string): 'ltr' | 'rtl' {
  return isArabicText(text) ? 'rtl' : 'ltr';
}

/**
 * Text measurement result
 */
export interface TextMeasurement {
  width: number;
  height: number;
}

/**
 * Text rendering configuration
 */
export interface TextConfig {
  text: string;
  fontSize: number;
  fontWeight?: string;
  fontFamily?: string;
  maxWidth?: number;
  lineHeight?: number;
  align?: 'left' | 'center' | 'right';
  direction?: 'ltr' | 'rtl';
}

/**
 * Canvas-based text measurement utility with RTL support
 * Uses CanvasRenderingContext2D.measureText for accurate measurements
 */
export class RTLTextRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  constructor() {
    this.canvas = document.createElement('canvas');
    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas 2D context for text measurement');
    }
    this.ctx = ctx;
  }

  /**
   * Sets font properties on canvas context
   */
  private setFont(fontSize: number, fontWeight: string = FONTS.WEIGHTS.REGULAR, fontFamily: string = FONTS.FAMILY) {
    this.ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  }

  /**
   * Measures single line of text
   */
  measureText(text: string, fontSize: number, fontWeight?: string, fontFamily?: string): TextMeasurement {
    this.setFont(fontSize, fontWeight, fontFamily);
    const metrics = this.ctx.measureText(text);
    
    return {
      width: metrics.width,
      height: fontSize * 1.2, // Approximate height based on font size
    };
  }

  /**
   * Wraps text to fit within maxWidth and returns lines
   */
  wrapText(text: string, maxWidth: number, fontSize: number, fontWeight?: string, fontFamily?: string): string[] {
    this.setFont(fontSize, fontWeight, fontFamily);
    
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = this.ctx.measureText(testLine);

      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [text];
  }

  /**
   * Measures text with wrapping and returns dimensions
   */
  measureWrappedText(config: TextConfig): { width: number; height: number; lines: string[] } {
    const {
      text,
      fontSize,
      fontWeight,
      fontFamily,
      maxWidth,
      lineHeight = fontSize * 1.4,
    } = config;

    if (!maxWidth) {
      const measurement = this.measureText(text, fontSize, fontWeight, fontFamily);
      return {
        width: measurement.width,
        height: measurement.height,
        lines: [text],
      };
    }

    const lines = this.wrapText(text, maxWidth, fontSize, fontWeight, fontFamily);
    const width = Math.min(
      maxWidth,
      Math.max(...lines.map(line => this.measureText(line, fontSize, fontWeight, fontFamily).width))
    );
    const height = lines.length * lineHeight;

    return { width, height, lines };
  }

  /**
   * Calculates text position based on alignment and direction
   */
  calculateTextPosition(
    x: number,
    y: number,
    textWidth: number,
    containerWidth: number,
    align: 'left' | 'center' | 'right' = 'left',
    direction: 'ltr' | 'rtl' = 'ltr'
  ): { x: number; align: 'left' | 'center' | 'right' } {
    let adjustedX = x;
    let adjustedAlign = align;

    if (direction === 'rtl') {
      // For RTL, flip alignment
      if (align === 'left') {
        adjustedAlign = 'right';
        adjustedX = x + containerWidth;
      } else if (align === 'right') {
        adjustedAlign = 'left';
        adjustedX = x;
      } else {
        adjustedAlign = 'center';
        adjustedX = x + containerWidth / 2;
      }
    } else {
      // LTR positioning
      if (align === 'center') {
        adjustedX = x + containerWidth / 2;
      } else if (align === 'right') {
        adjustedX = x + containerWidth;
      }
    }

    return { x: adjustedX, align: adjustedAlign };
  }
}

/**
 * Singleton instance for text rendering
 */
export const rtlTextRenderer = new RTLTextRenderer();
